import { supabase } from '../supabase';
import { normalizeCrmError } from './crmErrors';
import { deactivateSaleReceivable, syncSaleClientDebit, syncSaleReceivable } from './salesReceivables';
import { getStoredSelectedSiteId } from './sitePreferences';
import { buildAfipIvaBreakdown, selectBillableItems } from './afipIva';
import { getLocalDateKey } from './crmWorkflow';
import { fetchAllRows as fetchAllRowsPaginado, fetchRowsByIds } from './supabasePagination';

function unwrapResponse(label, response) {
  if (response?.error) {
    throw normalizeCrmError(response.error, `No se pudo resolver ${label}`);
  }
  return response?.data;
}

export function isMissingRelationError(error, relationName) {
  const needle = String(relationName || '').toLowerCase();
  const code = String(error?.code || error?.error?.code || '').toUpperCase();
  const status = Number(error?.status || error?.error?.status || 0);
  const message = [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!needle) return false;
  if (code === '42P01' || code === 'PGRST202' || code === 'PGRST205') {
    return message.includes(needle) || status === 404;
  }
  if (status === 404 && message.includes(needle)) return true;
  if (message.includes('could not find the table') && message.includes(needle)) return true;
  if (message.includes('could not find the function') && message.includes(needle)) return true;
  if (message.includes(`relation "public.${needle}`)) return true;
  if (message.includes(`relation "${needle}`)) return true;

  return false;
}

export function unwrapOptionalRows(label, response, relationName) {
  if (!response?.error) return response?.data || [];
  if (isMissingRelationError(response.error, relationName)) return [];
  throw normalizeCrmError(response.error, `No se pudo resolver ${label}`);
}

function isMissingColumnError(error, columnName) {
  const needle = String(columnName || '').toLowerCase();
  const shortNeedle = needle.split('.').pop();
  const code = String(error?.code || error?.error?.code || '').toUpperCase();
  const message = [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!needle) return false;

  return Boolean(
    code === '42703'
    || code === 'PGRST204'
    || message.includes(`column "${needle}"`)
    || message.includes(`column '${needle}'`)
    || message.includes(`column ${needle}`)
    || message.includes(`column "${shortNeedle}"`)
    || message.includes(`column '${shortNeedle}'`)
    || message.includes(`column ${shortNeedle}`)
    || message.includes(`could not find the '${needle}' column`)
    || message.includes(`could not find the "${needle}" column`)
    || message.includes(`could not find the '${shortNeedle}' column`)
    || message.includes(`could not find the "${shortNeedle}" column`)
  );
}

const DEFAULT_CRM_WORKDAYS = [1, 2, 3, 4, 5];

function normalizeCrmWorkdays(value) {
  if (!Array.isArray(value)) return [...DEFAULT_CRM_WORKDAYS];

  const normalized = value
    .map((day) => Number(day))
    .filter((day) => Number.isInteger(day) && day >= 1 && day <= 7);

  return normalized.length
    ? Array.from(new Set(normalized)).sort((left, right) => left - right)
    : [...DEFAULT_CRM_WORKDAYS];
}

async function fetchCrmReportProfiles(organizationId) {
  let response = await supabase
    .from('profiles')
    .select('id, full_name, email, crm_available_hours_per_day, crm_workdays')
    .eq('organization_id', organizationId)
    .order('full_name', { ascending: true });

  if (response.error && (
    isMissingColumnError(response.error, 'crm_available_hours_per_day')
    || isMissingColumnError(response.error, 'crm_workdays')
  )) {
    response = await supabase
      .from('profiles')
      .select('id, full_name, email, crm_available_hours_per_day')
      .eq('organization_id', organizationId)
      .order('full_name', { ascending: true });

    if (response.error && isMissingColumnError(response.error, 'crm_available_hours_per_day')) {
      response = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('organization_id', organizationId)
        .order('full_name', { ascending: true });
    }
  }

  return (unwrapResponse('reportProfiles', response) || []).map((row) => ({
    ...row,
    crm_workdays: normalizeCrmWorkdays(row.crm_workdays),
  }));
}

export function shouldFallbackCrmRpc(error, rpcName) {
  const needle = String(rpcName || '').toLowerCase();
  const status = Number(error?.status || error?.error?.status || 0);
  const code = String(error?.code || error?.error?.code || '').toUpperCase();
  const message = [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (isMissingRelationError(error, rpcName)) return true;
  if (status === 404 && (!needle || message.includes(needle) || message.includes('function'))) return true;
  if (code === 'PGRST202' || code === 'PGRST205') return true;
  if (needle && message.includes(`public.${needle}`)) return true;
  if (needle && message.includes(`/${needle}`)) return true;

  return false;
}

function toNumber(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function roundNumber(value, digits = 2) {
  const factor = 10 ** digits;
  return Math.round(toNumber(value) * factor) / factor;
}

function resolveCrmSaleSiteId(payload) {
  const explicitSiteId = String(payload?.site_id || '').trim();
  if (explicitSiteId) return explicitSiteId;

  const storedSiteId = String(getStoredSelectedSiteId() || '').trim();
  return storedSiteId || null;
}

function resolveCrmMovementType(billingSource) {
  const normalizedSource = String(billingSource || '').trim().toLowerCase();
  if (normalizedSource === 'crm_hito') return 'Liquidacion hito CRM';
  if (normalizedSource === 'crm_proyecto') return 'Liquidacion proyecto CRM';
  if (normalizedSource === 'crm_ticket') return 'Liquidacion ticket CRM';
  if (normalizedSource === 'crm_schedule') return 'Venta cronograma CRM';
  return 'Venta CRM';
}

async function syncCrmSaleContext({
  saleId,
  organizationId,
  clientId,
  siteId,
  totalAmount,
  dueDate,
  description,
  billingSource,
}) {
  if (!saleId || !organizationId) return;

  if (siteId) {
    const saleUpdateResponse = await supabase
      .from('sales')
      .update({ site_id: siteId })
      .eq('organization_id', organizationId)
      .eq('id', saleId);

    if (saleUpdateResponse.error) {
      throw normalizeCrmError(saleUpdateResponse.error, 'No se pudo asignar la sucursal de la venta CRM');
    }
  }

  if (!clientId) return;

  await syncSaleClientDebit({
    saleId,
    organizationId,
    clientId,
    totalAmount,
    movementType: resolveCrmMovementType(billingSource),
    observations: description || null,
    shouldApply: true,
  });

  await syncSaleReceivable({
    saleId,
    organizationId,
    clientId,
    siteId,
    source: billingSource || 'crm',
    description: description || null,
    dueDate,
    totalAmount,
    active: true,
  });
}

function frequencyToMonths(frequency) {
  if (frequency === 'yearly') return 12;
  if (frequency === 'quarterly') return 3;
  if (frequency === 'one_time') return 0;
  return 1;
}

function resolveDueDate(startDate, billingDay, cycleIndex) {
  // Todo en hora local: el parse `T00:00:00` es local, asi que los calculos y
  // el formateo tambien deben serlo (antes se mezclaba getUTCMonth con parse
  // local y el vencimiento podia caer un dia/mes corrido).
  const base = new Date(`${startDate || getLocalDateKey()}T00:00:00`);
  const year = base.getFullYear();
  const monthIndex = base.getMonth() + cycleIndex;
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const day = Math.min(Math.max(Number(billingDay || 1), 1), lastDay);
  return getLocalDateKey(new Date(year, monthIndex, day));
}

function isRuleTriggered(rule, cycleNumber) {
  const triggerValue = toNumber(rule.trigger_value);

  switch (rule.trigger_type) {
    case 'specific_cycle':
      return cycleNumber === triggerValue;
    case 'after_cycle':
      return cycleNumber >= Math.max(triggerValue, 1);
    case 'bundle_count':
      return triggerValue > 0 && cycleNumber % triggerValue === 0;
    case 'cycle_range':
    default: {
      const fromCycle = Math.max(toNumber(rule.applies_from_cycle) || 1, 1);
      const toCycle = rule.applies_to_cycle == null ? null : Math.max(toNumber(rule.applies_to_cycle), fromCycle);
      return cycleNumber >= fromCycle && (toCycle == null || cycleNumber <= toCycle);
    }
  }
}

function applyRuleEffect(amount, rule) {
  const currentAmount = Math.max(toNumber(amount), 0);
  const effectValue = toNumber(rule.effect_value);

  switch (rule.effect_type) {
    case 'fixed_discount':
      return Math.max(currentAmount - effectValue, 0);
    case 'set_final_amount':
      return Math.max(effectValue, 0);
    case 'free':
      return 0;
    case 'percent_discount':
    default:
      return Math.max(currentAmount - ((currentAmount * effectValue) / 100), 0);
  }
}

async function insertRowsWithFallback(tableName, rows, optionalColumns = []) {
  let currentRows = rows;
  let remainingColumns = [...optionalColumns];

  while (true) {
    const response = await supabase.from(tableName).insert(currentRows).select('*');
    if (!response.error) return response.data || [];

    const removableColumn = remainingColumns.find((columnName) => isMissingColumnError(response.error, columnName));
    if (!removableColumn) {
      throw normalizeCrmError(response.error, `No se pudo insertar en ${tableName}`);
    }

    console.warn(`[crmCommercial] insertRowsWithFallback: la tabla ${tableName} no tiene la columna "${removableColumn}"; se inserta sin ella`, {
      discardedColumn: removableColumn,
      remainingOptionalColumns: remainingColumns.filter((columnName) => columnName !== removableColumn),
    });

    remainingColumns = remainingColumns.filter((columnName) => columnName !== removableColumn);
    currentRows = currentRows.map((row) => {
      const nextRow = { ...row };
      delete nextRow[removableColumn];
      return nextRow;
    });
  }
}

async function createCrmAccountSaleCompatible(payload) {
  const organizationId = payload?.organization_id;
  const clientId = payload?.client_id;
  const totalAmount = Math.max(toNumber(payload?.total), 0);
  const siteId = resolveCrmSaleSiteId(payload);

  if (!organizationId || !clientId || totalAmount <= 0) {
    throw new Error('Faltan datos minimos para generar la venta CRM');
  }

  const createdAt = new Date().toISOString();
  const description = String(payload?.description || 'Cargo CRM').trim() || 'Cargo CRM';
  const billingSource = String(payload?.billing_source || 'crm_schedule').trim() || 'crm_schedule';

  const saleRows = await insertRowsWithFallback('sales', [{
    organization_id: organizationId,
    client_id: clientId,
    site_id: siteId,
    total: totalAmount,
    payment_method: 'Cuenta corriente CRM',
    status: 'completed',
    invoice_status: 'pendiente',
    active: true,
    user_id: payload?.actor_user_id || null,
    created_at: createdAt,
    billing_source: billingSource,
    invoice_sent_at: null,
    invoice_type_label: 'Venta CRM',
  }], ['site_id', 'billing_source', 'invoice_sent_at', 'invoice_type_label']);

  const sale = saleRows[0];
  if (!sale?.id) {
    throw new Error('No se pudo generar la venta CRM');
  }

  const itemRowsSource = Array.isArray(payload?.items) && payload.items.length > 0
    ? payload.items
    : [{ product_name_snapshot: description, quantity: 1, unit_price: totalAmount }];

  const itemRows = itemRowsSource.map((item) => ({
    sale_id: sale.id,
    organization_id: organizationId,
    product_id: null,
    variant_id: null,
    product_name_snapshot: String(item?.product_name_snapshot || description).trim() || description,
    quantity: Math.max(toNumber(item?.quantity) || 1, 0.0001),
    unit_price: Math.max(toNumber(item?.unit_price), 0),
  }));

  await insertRowsWithFallback('sale_items', itemRows, ['product_id', 'variant_id']);

  await syncCrmSaleContext({
    saleId: sale.id,
    organizationId,
    clientId,
    siteId,
    totalAmount,
    dueDate: payload?.due_date,
    description,
    billingSource,
  });

  try {
    await insertRowsWithFallback('crm_sale_links', [{
      organization_id: organizationId,
      sale_id: sale.id,
      agreement_id: payload?.agreement_id || null,
      schedule_id: payload?.schedule_id || null,
      project_id: payload?.project_id || null,
      milestone_id: payload?.milestone_id || null,
      ticket_id: payload?.ticket_id || null,
      charge_kind: payload?.charge_kind || 'cronograma',
    }], ['agreement_id', 'schedule_id', 'project_id', 'milestone_id', 'ticket_id', 'charge_kind']);
  } catch (error) {
    if (!isMissingRelationError(error, 'crm_sale_links')) {
      throw error;
    }
  }

  if (payload?.schedule_id) {
    const scheduleResponse = await supabase
      .from('crm_billing_schedules')
      .update({ status: 'generated' })
      .eq('organization_id', organizationId)
      .eq('id', payload.schedule_id);

    if (scheduleResponse.error && !isMissingRelationError(scheduleResponse.error, 'crm_billing_schedules')) {
      throw normalizeCrmError(scheduleResponse.error, 'No se pudo actualizar el cronograma CRM');
    }
  }

  if (payload?.milestone_id) {
    const milestoneResponse = await supabase
      .from('crm_project_milestones')
      .update({
        status: 'disparado',
        triggered_at: new Date().toISOString(),
        triggered_sale_id: sale.id,
      })
      .eq('organization_id', organizationId)
      .eq('id', payload.milestone_id);

    if (milestoneResponse.error && !isMissingRelationError(milestoneResponse.error, 'crm_project_milestones')) {
      throw normalizeCrmError(milestoneResponse.error, 'No se pudo actualizar el hito CRM');
    }
  }

  if (payload?.project_id && payload?.charge_kind === 'proyecto') {
    const projectResponse = await supabase
      .from('crm_projects')
      .update({
        liquidated_at: new Date().toISOString(),
        liquidated_sale_id: sale.id,
      })
      .eq('organization_id', organizationId)
      .eq('id', payload.project_id);

    if (projectResponse.error && !isMissingRelationError(projectResponse.error, 'crm_projects')) {
      throw normalizeCrmError(projectResponse.error, 'No se pudo marcar el proyecto como liquidado');
    }
  }

  if (payload?.ticket_id && payload?.charge_kind === 'ticket') {
    const ticketResponse = await supabase
      .from('crm_tickets')
      .update({
        liquidated_at: new Date().toISOString(),
        liquidated_sale_id: sale.id,
      })
      .eq('organization_id', organizationId)
      .eq('id', payload.ticket_id);

    if (ticketResponse.error && !isMissingRelationError(ticketResponse.error, 'crm_tickets')) {
      throw normalizeCrmError(ticketResponse.error, 'No se pudo marcar el ticket como liquidado');
    }
  }

  return sale;
}

async function processCrmDueMilestonesCompatible(organizationId, processDate = null) {
  const targetDate = processDate || getLocalDateKey();
  const milestonesResponse = await supabase
    .from('crm_project_milestones')
    .select('id, project_id, name, amount, due_date, status, crm_projects!inner(id, client_id, name)')
    .eq('organization_id', organizationId)
    .eq('status', 'pendiente')
    .lte('due_date', targetDate)
    .order('due_date', { ascending: true });

  const milestoneRows = unwrapOptionalRows('processCrmDueMilestonesCompatibleRows', milestonesResponse, 'crm_project_milestones');
  let processed = 0;

  for (const milestoneRow of milestoneRows) {
    const projectRow = Array.isArray(milestoneRow.crm_projects) ? milestoneRow.crm_projects[0] : milestoneRow.crm_projects;
    if (!projectRow?.client_id || !milestoneRow?.id) continue;

    // Optimistic locking: reclamamos el hito ANTES de crear la venta. El
    // filtro por status='pendiente' garantiza que solo un proceso concurrente
    // obtiene la fila; si no vuelve nada, otro proceso ya lo esta procesando.
    const claimResponse = await supabase
      .from('crm_project_milestones')
      .update({ status: 'disparado', triggered_at: new Date().toISOString() })
      .eq('organization_id', organizationId)
      .eq('id', milestoneRow.id)
      .eq('status', 'pendiente')
      .select('id');

    if (claimResponse.error) {
      throw normalizeCrmError(claimResponse.error, 'No se pudo reservar el hito CRM para su liquidacion');
    }
    if (!(claimResponse.data || []).length) continue;

    try {
      // createCrmAccountSaleCompatible completa triggered_sale_id sobre el hito
      // ya reclamado (via payload.milestone_id).
      await createCrmAccountSaleCompatible({
        organization_id: organizationId,
        client_id: projectRow.client_id,
        project_id: milestoneRow.project_id,
        milestone_id: milestoneRow.id,
        charge_kind: 'hito',
        billing_source: 'crm_hito',
        description: `${milestoneRow.name || 'Hito'} - ${projectRow.name || 'Proyecto CRM'}`,
        due_date: milestoneRow.due_date,
        total: milestoneRow.amount,
        items: [{
          product_name_snapshot: `${milestoneRow.name || 'Hito'} - ${projectRow.name || 'Proyecto CRM'}`,
          quantity: 1,
          unit_price: milestoneRow.amount,
        }],
      });
    } catch (error) {
      // Si la venta fallo, liberamos el hito para que pueda reintentarse.
      // Solo si no quedo vinculado a una venta (triggered_sale_id null): si la
      // venta llego a crearse y fallo un paso posterior, no lo reabrimos para
      // evitar doble facturacion.
      await supabase
        .from('crm_project_milestones')
        .update({ status: 'pendiente', triggered_at: null })
        .eq('organization_id', organizationId)
        .eq('id', milestoneRow.id)
        .eq('status', 'disparado')
        .is('triggered_sale_id', null);
      throw error;
    }

    processed += 1;
  }

  return { processed, process_date: targetDate };
}

async function processCrmDueProjectLiquidationsCompatible(organizationId, processDate = null) {
  const targetDate = processDate || getLocalDateKey();
  const projectsResponse = await supabase
    .from('crm_projects')
    .select('id, client_id, name, billing_type, budget_amount, liquidation_due_date, liquidated_sale_id')
    .eq('organization_id', organizationId)
    // El abono mensual es recurrente y lo resuelve el RPC
    // crm_process_due_project_liquidations, que avanza el ciclo mes a mes.
    // Este fallback solo cubre el cobro unico (one shot): si tomara los
    // abonos los cerraria con un unico cobro y romperia la recurrencia.
    .neq('billing_type', 'abono_mensual')
    .not('liquidation_due_date', 'is', null)
    .is('liquidated_sale_id', null)
    .lte('liquidation_due_date', targetDate);

  const projectRows = unwrapResponse('processCrmDueProjectLiquidationsCompatibleRows', projectsResponse) || [];
  let processed = 0;

  for (const projectRow of projectRows) {
    const total = Math.max(toNumber(projectRow.budget_amount), 0);
    if (!projectRow.client_id || total <= 0) continue;

    // Optimistic locking: reclamamos el proyecto marcando liquidated_at ANTES
    // de crear la venta. El filtro liquidated_sale_id IS NULL + liquidated_at
    // IS NULL garantiza que solo un proceso concurrente gana la fila.
    const claimResponse = await supabase
      .from('crm_projects')
      .update({ liquidated_at: new Date().toISOString() })
      .eq('organization_id', organizationId)
      .eq('id', projectRow.id)
      .is('liquidated_sale_id', null)
      .is('liquidated_at', null)
      .select('id');

    if (claimResponse.error) {
      throw normalizeCrmError(claimResponse.error, 'No se pudo reservar el proyecto CRM para su liquidacion');
    }
    if (!(claimResponse.data || []).length) continue;

    try {
      // createCrmAccountSaleCompatible completa liquidated_sale_id sobre el
      // proyecto ya reclamado (via project_id + charge_kind 'proyecto').
      await createCrmAccountSaleCompatible({
        organization_id: organizationId,
        client_id: projectRow.client_id,
        project_id: projectRow.id,
        charge_kind: 'proyecto',
        billing_source: 'crm_proyecto',
        description: `${projectRow.name || 'Proyecto CRM'} - Liquidacion proyecto`,
        due_date: projectRow.liquidation_due_date,
        total,
        items: [{
          product_name_snapshot: `${projectRow.name || 'Proyecto CRM'} - Liquidacion proyecto`,
          quantity: 1,
          unit_price: total,
        }],
      });
    } catch (error) {
      // Si la venta fallo, liberamos el proyecto para que pueda reintentarse.
      await supabase
        .from('crm_projects')
        .update({ liquidated_at: null, liquidated_sale_id: null })
        .eq('organization_id', organizationId)
        .eq('id', projectRow.id)
        .is('liquidated_sale_id', null);
      throw error;
    }

    processed += 1;
  }

  return { processed, process_date: targetDate };
}

function resolveAfipCbteTipo(emitterCondition, receiverCondition) {
  const emitter = String(emitterCondition || '').toLowerCase();
  const receiver = String(receiverCondition || '').toLowerCase();
  const emitterIsMono = emitter.includes('monotrib');
  const emitterIsRi = emitter.includes('responsable') && emitter.includes('inscrip');

  if (emitterIsMono) return 11;
  if (emitterIsRi) {
    if ((receiver.includes('responsable') && receiver.includes('inscrip')) || receiver.includes('monotrib')) return 1;
    return 6;
  }

  return 11;
}

function resolveAfipCbteLabel(cbteTipo) {
  if (Number(cbteTipo) === 1) return 'Factura A';
  if (Number(cbteTipo) === 6) return 'Factura B';
  if (Number(cbteTipo) === 11) return 'Factura C';
  return 'Comprobante AFIP';
}

function resolveAfipReceiverConditionId(receiverCondition) {
  const receiver = String(receiverCondition || '').toLowerCase();
  if (receiver.includes('responsable') && receiver.includes('inscrip')) return 1;
  if (receiver.includes('exento')) return 4;
  if (receiver.includes('monotrib')) return 6;
  return 5;
}

export function formatCrmCurrency(value) {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export function formatCrmDate(value) {
  if (!value) return 'Sin fecha';
  return new Date(`${value}T00:00:00`).toLocaleDateString('es-AR');
}

export function buildDateRange(daysBack = 30) {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - Math.max(daysBack, 1));

  return {
    from: getLocalDateKey(from),
    to: getLocalDateKey(today),
  };
}

async function saveCrmCommercialAgreementCompatible(payload) {
  if (!payload?.organization_id || !payload?.client_id || !String(payload?.name || '').trim()) {
    throw new Error('Cliente y nombre del acuerdo son obligatorios');
  }

  const agreementPayload = {
    organization_id: payload.organization_id,
    client_id: payload.client_id,
    name: String(payload.name || '').trim(),
    agreement_type: payload.agreement_type || 'recurring',
    start_date: payload.start_date || getLocalDateKey(),
    end_date: payload.end_date || null,
    billing_day: Math.max(toNumber(payload.billing_day) || 1, 1),
    currency: payload.currency || 'ARS',
    status: payload.status || 'draft',
    notes: payload.notes || null,
  };

  // Defensa en profundidad ademas del RLS: el update queda acotado a la
  // organizacion del payload.
  const agreementResponse = payload.id
    ? await supabase.from('crm_commercial_agreements').update(agreementPayload).eq('organization_id', payload.organization_id).eq('id', payload.id).select('*').single()
    : await supabase.from('crm_commercial_agreements').insert(agreementPayload).select('*').single();
  const agreement = unwrapResponse('saveCommercialAgreementCompatibleAgreement', agreementResponse);

  const deleteRulesResponse = await supabase.from('crm_agreement_pricing_rules').delete().eq('agreement_id', agreement.id);
  if (deleteRulesResponse.error && !isMissingRelationError(deleteRulesResponse.error, 'crm_agreement_pricing_rules')) {
    throw normalizeCrmError(deleteRulesResponse.error, 'No se pudieron limpiar las reglas del acuerdo');
  }

  const deleteItemsResponse = await supabase.from('crm_commercial_agreement_items').delete().eq('agreement_id', agreement.id);
  if (deleteItemsResponse.error && !isMissingRelationError(deleteItemsResponse.error, 'crm_commercial_agreement_items')) {
    throw normalizeCrmError(deleteItemsResponse.error, 'No se pudieron limpiar los items del acuerdo');
  }

  const itemRows = (payload.items || []).map((item) => ({
    organization_id: payload.organization_id,
    agreement_id: agreement.id,
    client_service_id: item.client_service_id || null,
    concept: String(item.concept || '').trim(),
    quantity: Math.max(toNumber(item.quantity) || 1, 1),
    unit_price: Math.max(toNumber(item.unit_price), 0),
    billing_frequency: item.billing_frequency || 'monthly',
    start_date: item.start_date || agreement.start_date,
    end_date: item.end_date || null,
    tax_rate: Math.max(toNumber(item.tax_rate), 0),
    discount_type: item.discount_type || null,
    discount_value: item.discount_value == null ? null : Math.max(toNumber(item.discount_value), 0),
  })).filter((item) => item.concept);

  const insertedItems = itemRows.length
    ? await insertRowsWithFallback('crm_commercial_agreement_items', itemRows)
    : [];

  const ruleRows = (payload.rules || []).map((rule) => ({
    organization_id: payload.organization_id,
    agreement_id: agreement.id,
    agreement_item_id: rule.item_index ? insertedItems[Math.max(Number(rule.item_index) - 1, 0)]?.id || null : null,
    rule_name: String(rule.rule_name || '').trim(),
    trigger_type: rule.trigger_type || 'cycle_range',
    trigger_value: rule.trigger_value == null ? null : toNumber(rule.trigger_value),
    applies_from_cycle: Math.max(toNumber(rule.applies_from_cycle) || 1, 1),
    applies_to_cycle: rule.applies_to_cycle == null ? null : Math.max(toNumber(rule.applies_to_cycle), 1),
    effect_type: rule.effect_type || 'percent_discount',
    effect_value: Math.max(toNumber(rule.effect_value), 0),
    bundle_group: rule.bundle_group || null,
    priority: Math.max(toNumber(rule.priority) || 100, 0),
    is_active: rule.is_active !== false,
  })).filter((rule) => rule.rule_name);

  if (ruleRows.length) {
    await insertRowsWithFallback('crm_agreement_pricing_rules', ruleRows);
  }

  return {
    agreement_id: agreement.id,
    items_saved: insertedItems.length,
    rules_saved: ruleRows.length,
  };
}

async function generateCrmBillingScheduleCompatible(agreementId, cycles = 12, replaceExisting = true) {
  const agreementResponse = await supabase.from('crm_commercial_agreements').select('*').eq('id', agreementId).single();
  const agreement = unwrapResponse('generateBillingScheduleCompatibleAgreement', agreementResponse);
  const itemsResponse = await supabase.from('crm_commercial_agreement_items').select('*').eq('agreement_id', agreementId);
  const rulesResponse = await supabase.from('crm_agreement_pricing_rules').select('*').eq('agreement_id', agreementId).order('priority', { ascending: true });
  const items = unwrapOptionalRows('generateBillingScheduleCompatibleItems', itemsResponse, 'crm_commercial_agreement_items');
  const rules = unwrapOptionalRows('generateBillingScheduleCompatibleRules', rulesResponse, 'crm_agreement_pricing_rules');

  if (replaceExisting) {
    // Solo se reemplazan los ciclos pendientes: los ya facturados (generated),
    // pagados o cancelados quedan intactos para no perder historia contable.
    const deleteResponse = await supabase
      .from('crm_billing_schedules')
      .delete()
      .eq('agreement_id', agreementId)
      .eq('status', 'pending');
    if (deleteResponse.error && !isMissingRelationError(deleteResponse.error, 'crm_billing_schedules')) {
      throw normalizeCrmError(deleteResponse.error, 'No se pudo reemplazar la programacion existente');
    }
  }

  // Fechas ya ocupadas por ciclos existentes (tras el delete, los que quedan
  // son los no pendientes cuando replaceExisting=true). No se recrean ciclos
  // sobre esas fechas para evitar duplicar cobros ya generados.
  const existingResponse = await supabase
    .from('crm_billing_schedules')
    .select('id, due_date, status')
    .eq('agreement_id', agreementId);
  const existingSchedules = unwrapOptionalRows('generateBillingScheduleCompatibleExisting', existingResponse, 'crm_billing_schedules');
  const occupiedDueDates = new Set(existingSchedules.map((row) => String(row.due_date || '').slice(0, 10)).filter(Boolean));

  const scheduleRows = [];

  for (let cycleIndex = 0; cycleIndex < Math.max(toNumber(cycles) || 0, 0); cycleIndex += 1) {
    const cycleNumber = cycleIndex + 1;
    const dueDate = resolveDueDate(agreement.start_date, agreement.billing_day, cycleIndex);
    if (occupiedDueDates.has(dueDate)) continue;

    const subtotal = roundNumber(items.reduce((total, item) => {
      const stepMonths = frequencyToMonths(item.billing_frequency);
      const applies = stepMonths === 0 ? cycleIndex === 0 : cycleIndex % stepMonths === 0;
      if (!applies) return total;

      const baseAmount = toNumber(item.quantity || 1) * toNumber(item.unit_price || 0);
      const scopedRules = rules.filter((rule) => !rule.agreement_item_id || rule.agreement_item_id === item.id);
      const itemTotal = scopedRules.reduce((amount, rule) => (
        rule.is_active === false || !isRuleTriggered(rule, cycleNumber)
          ? amount
          : applyRuleEffect(amount, rule)
      ), baseAmount);

      return total + itemTotal;
    }, 0), 2);

    scheduleRows.push({
      organization_id: agreement.organization_id,
      agreement_id: agreement.id,
      cycle_number: cycleNumber,
      due_date: dueDate,
      status: 'pending',
      currency: agreement.currency || 'ARS',
      subtotal_amount: subtotal,
      final_amount: subtotal,
    });
  }

  const insertedRows = scheduleRows.length
    ? await insertRowsWithFallback('crm_billing_schedules', scheduleRows, ['currency', 'subtotal_amount'])
    : [];

  return {
    agreement_id: agreement.id,
    schedules_generated: insertedRows.length,
  };
}

async function generateCrmInvoiceBatchCompatible(payload) {
  const scheduleIds = (payload?.schedule_ids || []).filter(Boolean);
  if (!payload?.agreement_id || !scheduleIds.length) {
    throw new Error('Selecciona al menos un ciclo para facturar');
  }

  const schedulesResponse = await supabase
    .from('crm_billing_schedules')
    .select('*')
    .eq('agreement_id', payload.agreement_id)
    .in('id', scheduleIds);
  const allSchedules = unwrapResponse('generateInvoiceBatchCompatibleSchedules', schedulesResponse) || [];
  // Solo se facturan ciclos pendientes: si otro proceso ya los marco como
  // generated (o estan pagados/cancelados), se descartan para no duplicar.
  const schedules = allSchedules.filter((schedule) => String(schedule.status || '').toLowerCase() === 'pending');
  if (!schedules.length) {
    throw new Error('Los ciclos seleccionados ya fueron facturados o no estan pendientes');
  }
  const agreementResponse = await supabase
    .from('crm_commercial_agreements')
    .select('*')
    .eq('id', payload.agreement_id)
    .maybeSingle();
  const agreement = unwrapResponse('generateInvoiceBatchCompatibleAgreement', agreementResponse);

  if (!agreement?.client_id) {
    throw new Error('El acuerdo seleccionado no tiene cliente asociado');
  }

  const itemsResponse = await supabase
    .from('crm_commercial_agreement_items')
    .select('*')
    .eq('agreement_id', payload.agreement_id);
  const agreementItems = unwrapOptionalRows('generateInvoiceBatchCompatibleItems', itemsResponse, 'crm_commercial_agreement_items');
  const agreementItemsMap = Object.fromEntries((agreementItems || []).map((item) => [item.id, item]));

  const saleItems = schedules.map((schedule) => {
    const agreementItem = agreementItemsMap[schedule.agreement_item_id] || null;
    return {
      product_name_snapshot: `${agreementItem?.concept || 'Ciclo CRM'} · Ciclo ${schedule.cycle_number || 0}`,
      // final_amount ya es el total del ciclo: la linea va con cantidad 1 para
      // que la suma de items (quantity * unit_price) coincida con el total de
      // la venta y con el desglose IVA que se envia a AFIP.
      quantity: 1,
      unit_price: toNumber(schedule.final_amount || 0),
    };
  });

  const totalAmount = roundNumber(schedules.reduce((acc, schedule) => acc + toNumber(schedule.final_amount || 0), 0), 2);
  const sortedSchedules = [...schedules]
    .sort((left, right) => String(left.due_date || '').localeCompare(String(right.due_date || '')));
  const latestDueDate = sortedSchedules[sortedSchedules.length - 1]?.due_date || payload.issue_date || getLocalDateKey();

  const sale = await createCrmAccountSaleCompatible({
    organization_id: payload.organization_id || agreement.organization_id,
    client_id: agreement.client_id,
    site_id: payload?.site_id || null,
    due_date: latestDueDate,
    total: totalAmount,
    description: `${agreement.name || 'Programacion CRM'}${schedules.length > 1 ? ` (${schedules.length} ciclos)` : ''}`,
    billing_source: 'crm_schedule',
    actor_user_id: payload?.actor_user_id || null,
    items: saleItems,
  });

  try {
    const linkRows = schedules.map((schedule) => ({
      organization_id: payload.organization_id || schedule.organization_id,
      sale_id: sale.id,
      agreement_id: payload.agreement_id,
      schedule_id: schedule.id,
      project_id: null,
      milestone_id: null,
      charge_kind: 'cronograma',
    }));
    await insertRowsWithFallback('crm_sale_links', linkRows, ['agreement_id', 'schedule_id', 'project_id', 'milestone_id', 'charge_kind']);
  } catch (error) {
    if (!isMissingRelationError(error, 'crm_sale_links')) {
      throw error;
    }
  }

  // Update condicional (solo filas todavia pending): si otro proceso facturo
  // en paralelo alguno de estos ciclos, no lo re-marcamos a ciegas.
  const updateSchedulesResponse = await supabase
    .from('crm_billing_schedules')
    .update({ status: 'generated' })
    .in('id', schedules.map((schedule) => schedule.id))
    .eq('status', 'pending');
  if (updateSchedulesResponse.error && !isMissingRelationError(updateSchedulesResponse.error, 'crm_billing_schedules')) {
    throw normalizeCrmError(updateSchedulesResponse.error, 'No se pudieron actualizar los ciclos facturados');
  }

  return {
    agreement_id: payload.agreement_id,
    sales_created: sale?.id ? 1 : 0,
    sale_ids: sale?.id ? [sale.id] : [],
    invoices_created: sale?.id ? 1 : 0,
  };
}

// Tiempo maximo de espera del edge function AFIP y umbral a partir del cual un
// 'enviado' sin respuesta se considera colgado y habilita el reintento.
const CRM_AFIP_FETCH_TIMEOUT_MS = 60000;
export const CRM_AFIP_SEND_STALE_MS = 5 * 60 * 1000;

export function isCrmAfipSendStale(invoiceSentAt) {
  if (!invoiceSentAt) return true;
  const sentAtMs = new Date(invoiceSentAt).getTime();
  if (!Number.isFinite(sentAtMs)) return true;
  return Date.now() - sentAtMs > CRM_AFIP_SEND_STALE_MS;
}

export async function emitCrmSaleAfipInvoice({ saleId, organizationId }) {
  if (!saleId) {
    throw new Error('Falta identificar la venta a facturar');
  }

  let markedAsSent = false;

  try {
    // Defensa en profundidad ademas del RLS: si el caller informa la
    // organizacion, la venta se busca acotada a ella.
    let saleQuery = supabase
      .from('sales')
      .select('id, organization_id, client_id, total, invoice_status, invoice_sent_at, billing_entity_id, invoice_cbte_tipo')
      .eq('id', saleId);
    if (organizationId) saleQuery = saleQuery.eq('organization_id', organizationId);
    const saleResponse = await saleQuery.maybeSingle();
    const sale = unwrapResponse('crmSaleToInvoice', saleResponse);

    if (!sale?.id) {
      throw new Error('La venta seleccionada ya no existe');
    }

    const currentInvoiceStatus = String(sale.invoice_status || '').toLowerCase();
    if (currentInvoiceStatus === 'facturado') {
      return { sale_id: sale.id, invoice_status: 'facturado' };
    }
    if (currentInvoiceStatus === 'enviado' && !isCrmAfipSendStale(sale.invoice_sent_at)) {
      // Envio reciente en curso: bloqueamos el doble envio. Si el envio quedo
      // colgado (mas de CRM_AFIP_SEND_STALE_MS sin resolverse), permitimos
      // reintentar en lugar de dejar la venta trabada en 'enviado'.
      throw new Error('Esta venta ya se esta enviando a AFIP. Espera unos minutos y reintenta.');
    }

    const clientResponse = sale.client_id
      ? await supabase
        .from('clients')
        .select('id, full_name, doc_number, doc_type, tax_condition')
        .eq('id', sale.client_id)
        .maybeSingle()
      : { data: null, error: null };
    const client = clientResponse?.error
      ? unwrapResponse('crmSaleInvoiceClient', clientResponse)
      : (clientResponse?.data || null);

    let billingEntityId = sale.billing_entity_id || null;
    if (!billingEntityId && (organizationId || sale.organization_id)) {
      const organizationResponse = await supabase
        .from('organizations')
        .select('default_billing_entity_id')
        .eq('id', organizationId || sale.organization_id)
        .maybeSingle();
      const organization = unwrapResponse('crmSaleInvoiceOrganization', organizationResponse);
      billingEntityId = organization?.default_billing_entity_id || null;
    }

    if (!billingEntityId) {
      throw new Error('La venta no tiene emisor fiscal asignado. Configura un emisor por defecto antes de emitir AFIP.');
    }

    const entityResponse = await supabase
      .from('billing_entities')
      .select('id, tax_condition, fiscal_configuration')
      .eq('id', billingEntityId)
      .maybeSingle();
    const billingEntity = unwrapResponse('crmSaleInvoiceBillingEntity', entityResponse);

    if (!billingEntity?.id) {
      throw new Error('No se encontro el emisor fiscal configurado para esta venta');
    }

    const saleItemsResponse = await supabase
      .from('sale_items')
      .select('id, sale_id, product_name_snapshot, quantity, unit_price, subtotal, combo_role, iva_percentage, products!vinc_prod(iva_percentage)')
      .eq('sale_id', sale.id);
    const saleItems = unwrapResponse('crmSaleInvoiceItems', saleItemsResponse) || [];

    const emitterCondition = billingEntity.fiscal_configuration || billingEntity.tax_condition || '';
    const receiverCondition = client?.tax_condition || 'Consumidor Final';
    const documentType = client?.doc_type || 'DNI';
    const cbteTipo = Number(sale.invoice_cbte_tipo || resolveAfipCbteTipo(emitterCondition, receiverCondition));

    if (cbteTipo === 1 && String(documentType || '').toUpperCase() !== 'CUIT') {
      throw new Error('Para Factura A el cliente debe tener CUIT cargado');
    }

    const sentAtIso = new Date().toISOString();
    const preflightUpdate = await supabase
      .from('sales')
      .update({
        invoice_status: 'enviado',
        invoice_sent_at: sentAtIso,
        billing_entity_id: billingEntity.id,
        invoice_cbte_tipo: cbteTipo,
        invoice_type_label: resolveAfipCbteLabel(cbteTipo),
      })
      .eq('organization_id', sale.organization_id)
      .eq('id', sale.id);
    if (preflightUpdate.error) {
      throw normalizeCrmError(preflightUpdate.error, 'No se pudo preparar la venta para AFIP');
    }
    markedAsSent = true;

    const sessionResponse = await supabase.auth.getSession();
    const token = sessionResponse?.data?.session?.access_token || '';
    if (!token) {
      throw new Error('No se pudo validar la sesion para emitir AFIP');
    }

    const traceId = (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function')
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    let projectUrl = supabase.supabaseUrl;
    if (projectUrl.endsWith('/')) projectUrl = projectUrl.slice(0, -1);

    // IVA por alícuota: salvo comprobante C (monotributo), enviamos el desglose
    // explícito para que el CAE coincida con el detalle de la venta.
    const crmEsComprobanteC = cbteTipo === 11 || cbteTipo === 13;
    const crmBillableItems = selectBillableItems(saleItems);
    const crmIvaBreakdown = crmEsComprobanteC
      ? null
      : buildAfipIvaBreakdown(saleItems, { pricesIncludeIva: true, saleTotal: Number(sale.total || 0) });

    // Timeout defensivo: si el edge function no responde, abortamos el fetch
    // para que el catch externo devuelva invoice_status a 'error' y el boton
    // de reintento quede disponible (antes la venta quedaba colgada en
    // 'enviado' para siempre).
    const abortController = new AbortController();
    const fetchTimeoutId = setTimeout(() => abortController.abort(), CRM_AFIP_FETCH_TIMEOUT_MS);

    let response;
    try {
      response = await fetch(`${projectUrl}/functions/v1/arca-facturar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        signal: abortController.signal,
        body: JSON.stringify({
        sale_id: sale.id,
        trace_id: traceId,
        billing_entity_id: billingEntity.id,
        cbte_tipo: cbteTipo,
        condicion_iva_receptor_id: resolveAfipReceiverConditionId(receiverCondition),
        condicion_iva_receptor: receiverCondition,
        total: Number(sale.total || 0),
        prices_include_iva: true,
        cliente: {
          doc_tipo: documentType,
          doc_nro: client?.doc_number || 0,
          tax_condition: receiverCondition,
        },
        items: crmBillableItems,
        ...(crmIvaBreakdown ? { iva: crmIvaBreakdown.iva, totals: crmIvaBreakdown.totals } : {}),
        }),
      });
    } catch (fetchError) {
      if (abortController.signal.aborted) {
        throw new Error('AFIP no respondio a tiempo. La venta quedo en estado de error: reintenta en unos minutos.');
      }
      throw new Error(fetchError?.message || 'No se pudo contactar el servicio de facturacion AFIP');
    } finally {
      clearTimeout(fetchTimeoutId);
    }

    const responseData = await response.json().catch(() => ({}));
    if (!response.ok || !responseData?.ok) {
      throw new Error(responseData?.message || 'AFIP rechazo la solicitud');
    }

    return {
      ok: true,
      sale_id: sale.id,
      invoice_status: 'enviado',
    };
  } catch (error) {
    if (markedAsSent) {
      let revertQuery = supabase.from('sales').update({ invoice_status: 'error' }).eq('id', saleId);
      if (organizationId) revertQuery = revertQuery.eq('organization_id', organizationId);
      const revertResponse = await revertQuery;
      if (revertResponse.error) {
        console.error('[crmCommercial] emitCrmSaleAfipInvoice: no se pudo marcar la venta con estado de error tras el fallo AFIP', {
          saleId,
          message: revertResponse.error?.message || revertResponse.error,
        });
      }
    }
    throw error;
  }
}

export async function fetchCrmCommercialHub(organizationId) {
  const [
    clientsResponse,
    servicesResponse,
    agreementsResponse,
    itemsResponse,
    rulesResponse,
    schedulesResponse,
    saleLinksResponse,
  ] = await Promise.all([
    supabase.from('clients').select('id, full_name, email, doc_number, is_active').eq('organization_id', organizationId).order('full_name', { ascending: true }),
    supabase.from('crm_client_services').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }),
    supabase.from('crm_commercial_agreements').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }),
    supabase.from('crm_commercial_agreement_items').select('*').eq('organization_id', organizationId).order('created_at', { ascending: true }),
    supabase.from('crm_agreement_pricing_rules').select('*').eq('organization_id', organizationId).order('priority', { ascending: true }),
    supabase.from('crm_billing_schedules').select('*').eq('organization_id', organizationId).order('due_date', { ascending: true }),
    supabase.from('crm_sale_links').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }),
  ]);

  const saleLinks = unwrapOptionalRows('commercialSaleLinks', saleLinksResponse, 'crm_sale_links');
  const saleIds = Array.from(new Set((saleLinks || []).map((row) => row.sale_id).filter(Boolean)));

  let sales = [];
  let saleItems = [];

  if (saleIds.length > 0) {
    const [salesResponse, saleItemsResponse] = await Promise.all([
      supabase
        .from('sales')
        .select('id, client_id, total, status, active, payment_method, invoice_status, numero_factura, created_at, billing_source, billing_entity_id, invoice_sent_at, cae, cae_vencimiento, invoice_cbte_tipo, invoice_type_label')
        .eq('organization_id', organizationId)
        .in('id', saleIds)
        .order('created_at', { ascending: false }),
      supabase
        .from('sale_items')
        .select('id, sale_id, product_name_snapshot, quantity, unit_price')
        .eq('organization_id', organizationId)
        .in('sale_id', saleIds),
    ]);

    sales = unwrapResponse('commercialSales', salesResponse) || [];
    saleItems = unwrapResponse('commercialSaleItems', saleItemsResponse) || [];
  }

  return {
    clients: unwrapResponse('commercialClients', clientsResponse) || [],
    services: unwrapOptionalRows('commercialServices', servicesResponse, 'crm_client_services'),
    agreements: unwrapOptionalRows('commercialAgreements', agreementsResponse, 'crm_commercial_agreements'),
    items: unwrapOptionalRows('commercialAgreementItems', itemsResponse, 'crm_commercial_agreement_items'),
    rules: unwrapOptionalRows('commercialRules', rulesResponse, 'crm_agreement_pricing_rules'),
    schedules: unwrapOptionalRows('commercialSchedules', schedulesResponse, 'crm_billing_schedules'),
    saleLinks,
    sales,
    saleItems,
    invoices: [],
    invoiceLines: [],
  };
}

export async function saveCrmClientService(payload) {
  const table = supabase.from('crm_client_services');
  const response = payload?.id
    ? await table.update(payload).eq('id', payload.id).select().single()
    : await table.insert(payload).select().single();

  return unwrapResponse('saveClientService', response);
}

export async function saveCrmCommercialAgreement(payload) {
  const response = await supabase.rpc('crm_save_commercial_agreement', { p_payload: payload });
  if (!response.error) {
    return response.data;
  }

  if (shouldFallbackCrmRpc(response.error, 'crm_save_commercial_agreement')) {
    return saveCrmCommercialAgreementCompatible(payload);
  }

  return unwrapResponse('saveCommercialAgreement', response);
}

export async function createCrmAccountSale(payload) {
  const normalizedPayload = {
    ...payload,
    site_id: resolveCrmSaleSiteId(payload),
  };

  const response = await supabase.rpc('crm_create_account_sale', { p_payload: normalizedPayload });
  if (!response.error) {
    await syncCrmSaleContext({
      saleId: response.data?.sale_id,
      organizationId: normalizedPayload.organization_id,
      clientId: response.data?.client_id || normalizedPayload.client_id,
      siteId: normalizedPayload.site_id,
      totalAmount: normalizedPayload.total,
      dueDate: normalizedPayload.due_date,
      description: normalizedPayload.description,
      billingSource: normalizedPayload.billing_source,
    });
    return response.data;
  }

  if (shouldFallbackCrmRpc(response.error, 'crm_create_account_sale')) {
    return createCrmAccountSaleCompatible(normalizedPayload);
  }

  return unwrapResponse('createCrmAccountSale', response);
}

export async function processCrmDueMilestones(organizationId, processDate = null) {
  const response = await supabase.rpc('crm_process_due_milestones', {
    p_organization_id: organizationId,
    p_process_date: processDate,
  });

  if (!response.error) {
    return response.data;
  }

  if (shouldFallbackCrmRpc(response.error, 'crm_process_due_milestones')) {
    return processCrmDueMilestonesCompatible(organizationId, processDate);
  }

  return unwrapResponse('processCrmDueMilestones', response);
}

/**
 * Cuenta los hitos de un proyecto que siguen pendientes de cobro
 * (status = 'pendiente': definidos pero todavía no facturados/disparados).
 * Se usa para impedir desactivar un proyecto que aún tiene hitos por cobrar.
 * Degrada a 0 si la tabla no existe en el tenant.
 */
export async function countPendingProjectMilestones(organizationId, projectId) {
  if (!organizationId || !projectId) return 0;
  const response = await supabase
    .from('crm_project_milestones')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .eq('project_id', projectId)
    .eq('status', 'pendiente');

  if (response.error) {
    if (isMissingRelationError(response.error, 'crm_project_milestones')) return 0;
    throw normalizeCrmError(response.error, 'No se pudieron verificar los hitos pendientes del proyecto');
  }
  return Number(response.count || 0);
}

export async function processCrmDueProjectLiquidations(organizationId, processDate = null) {
  const response = await supabase.rpc('crm_process_due_project_liquidations', {
    p_organization_id: organizationId,
    p_process_date: processDate,
  });

  if (!response.error) {
    return response.data;
  }

  if (shouldFallbackCrmRpc(response.error, 'crm_process_due_project_liquidations')) {
    return processCrmDueProjectLiquidationsCompatible(organizationId, processDate);
  }

  return unwrapResponse('processCrmDueProjectLiquidations', response);
}

export async function generateCrmBillingSchedule(agreementId, cycles = 12, replaceExisting = true) {
  const response = await supabase.rpc('crm_generate_billing_schedule', {
    p_agreement_id: agreementId,
    p_cycles: cycles,
    p_replace_existing: replaceExisting,
  });

  if (!response.error) {
    return response.data;
  }

  if (shouldFallbackCrmRpc(response.error, 'crm_generate_billing_schedule')) {
    return generateCrmBillingScheduleCompatible(agreementId, cycles, replaceExisting);
  }

  return unwrapResponse('generateBillingSchedule', response);
}

export async function generateCrmInvoiceBatch(payload) {
  return generateCrmInvoiceBatchCompatible({
    ...payload,
    site_id: resolveCrmSaleSiteId(payload),
  });
}

export async function retractCrmSaleMovement({ saleId, organizationId, actorUserId = null }) {
  if (!saleId || !organizationId) {
    return { skipped: true, reason: 'missing_context' };
  }

  const saleResponse = await supabase
    .from('sales')
    .select('id, organization_id, client_id, total, billing_source, invoice_status, active')
    .eq('organization_id', organizationId)
    .eq('id', saleId)
    .maybeSingle();

  const sale = unwrapResponse('crmSaleToRetract', saleResponse);
  if (!sale?.id) {
    return { skipped: true, reason: 'sale_not_found' };
  }

  const invoiceStatus = String(sale.invoice_status || '').trim().toLowerCase();
  if (invoiceStatus === 'facturado' || invoiceStatus === 'enviado') {
    throw new Error('No se puede retrotraer una liquidacion CRM ya enviada a facturacion');
  }

  const updateSaleResponse = await supabase
    .from('sales')
    .update({
      active: false,
      status: 'anulada',
      invoice_status: 'anulada',
    })
    .eq('organization_id', organizationId)
    .eq('id', saleId);

  if (updateSaleResponse.error) {
    throw normalizeCrmError(updateSaleResponse.error, 'No se pudo anular la venta CRM');
  }

  if (sale.client_id) {
    await syncSaleClientDebit({
      saleId,
      organizationId,
      clientId: sale.client_id,
      totalAmount: sale.total,
      movementType: resolveCrmMovementType(sale.billing_source),
      observations: `Anulacion de liquidacion CRM #${String(saleId).slice(0, 8)}.`,
      shouldApply: false,
    });
  }

  await deactivateSaleReceivable(saleId);
  await revertCrmSaleLinks({ saleId, organizationId, actorUserId });

  return {
    retracted: true,
    sale_id: saleId,
  };
}

export async function revertCrmSaleLinks({ saleId, organizationId, actorUserId = null }) {
  if (!saleId || !organizationId) {
    return { skipped: true, reason: 'missing_context' };
  }

  let scheduleIds = [];

  const saleLinksResponse = await supabase
    .from('crm_sale_links')
    .select('id, schedule_id')
    .eq('organization_id', organizationId)
    .eq('sale_id', saleId);

  if (!saleLinksResponse.error) {
    scheduleIds = Array.from(new Set((saleLinksResponse.data || []).map((row) => row.schedule_id).filter(Boolean)));
  } else if (!isMissingRelationError(saleLinksResponse.error, 'crm_sale_links')) {
    throw normalizeCrmError(saleLinksResponse.error, 'No se pudieron resolver los enlaces CRM de la venta');
  }

  const milestoneResponse = await supabase
    .from('crm_project_milestones')
    .update({
      status: 'pendiente',
      triggered_at: null,
      triggered_sale_id: null,
      updated_by: actorUserId,
    })
    .eq('organization_id', organizationId)
    .eq('triggered_sale_id', saleId);
  if (milestoneResponse.error && !isMissingRelationError(milestoneResponse.error, 'crm_project_milestones')) {
    throw normalizeCrmError(milestoneResponse.error, 'No se pudo revertir el hito CRM');
  }

  const projectResponse = await supabase
    .from('crm_projects')
    .update({
      liquidated_at: null,
      liquidated_sale_id: null,
      updated_by: actorUserId,
    })
    .eq('organization_id', organizationId)
    .eq('liquidated_sale_id', saleId);
  if (projectResponse.error && !isMissingRelationError(projectResponse.error, 'crm_projects')) {
    throw normalizeCrmError(projectResponse.error, 'No se pudo revertir la liquidacion del proyecto');
  }

  const ticketResponse = await supabase
    .from('crm_tickets')
    .update({
      liquidated_at: null,
      liquidated_sale_id: null,
      updated_by: actorUserId,
    })
    .eq('organization_id', organizationId)
    .eq('liquidated_sale_id', saleId);
  if (ticketResponse.error && !isMissingRelationError(ticketResponse.error, 'crm_tickets')) {
    throw normalizeCrmError(ticketResponse.error, 'No se pudo revertir la liquidacion del ticket');
  }

  if (scheduleIds.length > 0) {
    const scheduleResponse = await supabase
      .from('crm_billing_schedules')
      .update({
        status: 'pending',
        updated_by: actorUserId,
      })
      .eq('organization_id', organizationId)
      .in('id', scheduleIds);

    if (scheduleResponse.error && !isMissingRelationError(scheduleResponse.error, 'crm_billing_schedules')) {
      throw normalizeCrmError(scheduleResponse.error, 'No se pudo reabrir el cronograma CRM');
    }
  }

  const deleteLinksResponse = await supabase
    .from('crm_sale_links')
    .delete()
    .eq('organization_id', organizationId)
    .eq('sale_id', saleId);
  if (deleteLinksResponse.error && !isMissingRelationError(deleteLinksResponse.error, 'crm_sale_links')) {
    throw normalizeCrmError(deleteLinksResponse.error, 'No se pudieron limpiar los enlaces CRM de la venta');
  }

  return {
    reverted: true,
    schedule_count: scheduleIds.length,
  };
}

// Paginacion para reportes: PostgREST corta cada request en ~1000 filas, asi
// que las tablas con volumen se traen por paginas hasta completar el total
// (con tope de seguridad para no colgar el navegador).
//
// La implementacion se movio a `utils/supabasePagination.js` — habia una sola
// copia aca y el detalle de proyecto (crmWorkflow) no paginaba nada, asi que
// mostraba totales de horas subvaluados. Ese modulo ademas agrega el desempate
// por `id` en cada pagina; las queries de abajo ya lo traen escrito a mano y el
// ORDER BY duplicado es un no-op para Postgres.
async function fetchAllRows(queryBuilderFactory, options = {}) {
  return fetchAllRowsPaginado(queryBuilderFactory, { label: 'crmCommercial', ...options });
}

export async function fetchCrmReportsData(organizationId, fromDate, toDate) {
  // Rango de finalized_at (timestamptz): fin/inicio de dia en hora local
  // convertidos a ISO UTC, para no correr el corte del reporte segun la zona.
  const finalizedFromIso = new Date(`${fromDate}T00:00:00`).toISOString();
  const finalizedToIso = new Date(`${toDate}T23:59:59.999`).toISOString();

  const [
    timeEntriesResponse,
    capacityResponse,
    projectsResponse,
    milestonesResponse,
    projectTypesResponse,
    stagesResponse,
    ticketsResponse,
    ticketTypesResponse,
    ticketStatusesResponse,
    tasksResponse,
    ticketAssignmentsResponse,
    taskAssignmentsResponse,
    taskFinalizationsResponse,
    agreementsResponse,
    agreementItemsResponse,
    schedulesResponse,
    invoicesResponse,
    invoiceLinesResponse,
    clientsResponse,
    salesReceivablesResponse,
    meetingsResponse,
    meetingParticipantsResponse,
    meetingTopicsResponse,
    meetingTopicParticipantsResponse,
    projectExpensesResponse,
    projectFinancialSummaryResponse,
    ticketFinancialSummaryResponse,
    projectExpensesHistoricalResponse,
  ] = await Promise.all([
    // Tablas con volumen: se paginan con fetchAllRows (PostgREST corta cada
    // request en ~1000 filas). El orden estable (con desempate por id) es
    // necesario para que .range() no repita ni saltee filas entre paginas.
    fetchAllRows(() => supabase.from('crm_time_entries').select('*').eq('organization_id', organizationId).gte('entry_date', fromDate).lte('entry_date', toDate).order('id', { ascending: true }), { label: 'crm_time_entries' }),
    fetchAllRows(() => supabase.from('crm_user_capacity_calendar').select('*').eq('organization_id', organizationId).gte('work_date', fromDate).lte('work_date', toDate).order('id', { ascending: true }), { label: 'crm_user_capacity_calendar' }),
    fetchAllRows(() => supabase.from('crm_projects').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).order('id', { ascending: true }), { label: 'crm_projects' }),
    fetchAllRows(() => supabase.from('crm_project_milestones').select('*').eq('organization_id', organizationId).order('due_date', { ascending: true }).order('id', { ascending: true }), { label: 'crm_project_milestones' }),
    supabase.from('crm_project_types').select('*').eq('organization_id', organizationId).order('sort_order', { ascending: true }),
    fetchAllRows(() => supabase.from('crm_project_stages').select('*').eq('organization_id', organizationId).order('stage_order', { ascending: true }).order('id', { ascending: true }), { label: 'crm_project_stages' }),
    fetchAllRows(() => supabase.from('crm_tickets').select('*').eq('organization_id', organizationId).order('ticket_number', { ascending: false }).order('id', { ascending: true }), { label: 'crm_tickets' }),
    supabase.from('crm_ticket_types').select('*').eq('organization_id', organizationId).order('name', { ascending: true }),
    supabase.from('crm_ticket_statuses').select('*').eq('organization_id', organizationId).order('sort_order', { ascending: true }),
    fetchAllRows(() => supabase.from('crm_ticket_tasks').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).order('id', { ascending: true }), { label: 'crm_ticket_tasks' }),
    fetchAllRows(() => supabase.from('crm_ticket_assignments').select('*').eq('organization_id', organizationId).neq('status', 'cancelled').order('id', { ascending: true }), { label: 'crm_ticket_assignments' }),
    fetchAllRows(() => supabase.from('crm_ticket_task_assignments').select('*').eq('organization_id', organizationId).neq('status', 'cancelled').order('id', { ascending: true }), { label: 'crm_ticket_task_assignments' }),
    // finalized_at es timestamptz: el rango va en ISO UTC calculado desde el
    // dia local (finalizedFromIso/finalizedToIso) para no correr el corte.
    fetchAllRows(() => supabase.from('crm_ticket_task_finalizations').select('*').eq('organization_id', organizationId).gte('finalized_at', finalizedFromIso).lte('finalized_at', finalizedToIso).order('id', { ascending: true }), { label: 'crm_ticket_task_finalizations' }),
    fetchAllRows(() => supabase.from('crm_commercial_agreements').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).order('id', { ascending: true }), { label: 'crm_commercial_agreements' }),
    fetchAllRows(() => supabase.from('crm_commercial_agreement_items').select('*').eq('organization_id', organizationId).order('created_at', { ascending: true }).order('id', { ascending: true }), { label: 'crm_commercial_agreement_items' }),
    fetchAllRows(() => supabase.from('crm_billing_schedules').select('*').eq('organization_id', organizationId).gte('due_date', fromDate).lte('due_date', toDate).order('id', { ascending: true }), { label: 'crm_billing_schedules' }),
    fetchAllRows(() => supabase.from('crm_invoices').select('*').eq('organization_id', organizationId).gte('issue_date', fromDate).lte('issue_date', toDate).order('id', { ascending: true }), { label: 'crm_invoices' }),
    fetchAllRows(() => supabase.from('crm_invoice_lines').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).order('id', { ascending: true }), { label: 'crm_invoice_lines' }),
    fetchAllRows(() => supabase.from('clients').select('id, full_name, email, doc_number, is_active').eq('organization_id', organizationId).order('id', { ascending: true }), { label: 'clients' }),
    fetchAllRows(() => supabase.from('sales_receivables').select('id, sale_id, client_id, total_amount, pending_amount, settled_amount, due_date, is_active, created_at, updated_at').eq('organization_id', organizationId).order('id', { ascending: true }), { label: 'sales_receivables' }),
    fetchAllRows(() => supabase.from('crm_meetings').select('*').eq('organization_id', organizationId).gte('meeting_date', fromDate).lte('meeting_date', toDate).order('meeting_date', { ascending: false }).order('start_time', { ascending: false }).order('id', { ascending: true }), { label: 'crm_meetings' }),
    fetchAllRows(() => supabase.from('crm_meeting_participants').select('*').eq('organization_id', organizationId).order('created_at', { ascending: true }).order('id', { ascending: true }), { label: 'crm_meeting_participants' }),
    fetchAllRows(() => supabase.from('crm_meeting_topics').select('*').eq('organization_id', organizationId).order('topic_order', { ascending: true }).order('id', { ascending: true }), { label: 'crm_meeting_topics' }),
    fetchAllRows(() => supabase.from('crm_meeting_topic_participants').select('*').eq('organization_id', organizationId).order('created_at', { ascending: true }).order('id', { ascending: true }), { label: 'crm_meeting_topic_participants' }),
    fetchAllRows(() => supabase.from('crm_project_expenses').select('*').eq('organization_id', organizationId).gte('expense_date', fromDate).lte('expense_date', toDate).order('expense_date', { ascending: false }).order('id', { ascending: true }), { label: 'crm_project_expenses' }),
    // Totales HISTORICOS (sin filtro de fecha) agregados en el servidor. El resto
    // de este fetch esta acotado a [fromDate, toDate] porque el reporte es "del
    // periodo", pero un proyecto One shot (o un ticket) mide su consumo y
    // rentabilidad desde el inicio, no solo dentro del rango elegido -- si se usara
    // solo `timeEntries` acotado, el panel de progreso (que ya usa
    // consumed_total_minutes acumulado) y el panel financiero mostrarian numeros
    // de bases distintas para el mismo proyecto. Ver uso en CrmReportes.jsx.
    fetchAllRows(() => supabase.from('crm_project_financial_summary').select('project_id, total_hours_consumed, production_cost_amount, generated_value_amount, profitability_amount').eq('organization_id', organizationId), { label: 'crm_project_financial_summary', tieBreaker: 'project_id' }),
    fetchAllRows(() => supabase.from('crm_ticket_financial_summary').select('ticket_id, total_hours_consumed, production_cost_amount, generated_value_amount, profitability_amount').eq('organization_id', organizationId), { label: 'crm_ticket_financial_summary', tieBreaker: 'ticket_id' }),
    // Gastos directos SIN filtro de fecha, solo para la rentabilidad de proyectos
    // One shot: si el costo de horas es historico, los gastos tambien tienen que
    // serlo, o la rentabilidad mezcla periodos. El listado de gastos del reporte
    // sigue usando `projectExpenses` (acotado al rango).
    fetchAllRows(() => supabase.from('crm_project_expenses').select('id, project_id, amount').eq('organization_id', organizationId).order('id', { ascending: true }), { label: 'crm_project_expenses_historical' }),
  ]);

  /* `sales` acotada a las ventas que el CRM referencia. Traerla entera era el
     mismo bug de las 1000 filas un orden de magnitud más arriba: una
     organización de producción tiene 14.224 ventas activas creciendo ~4.300 por
     mes, y el reporte las usa solo para resolver `liquidated_sale_id` y
     `triggered_sale_id` (`salesMap` en CrmReportes). Sin esto, en ~8 meses la
     lectura tocaba el tope y el reporte entero fallaba.
     `sales_receivables` NO se puede acotar igual: sus paneles de cobranza
     (facturado/cobrado por mes, aging) miden TODA la organización, no solo lo
     del CRM. Queda paginada org-wide. */
  const crmSaleIds = [
    ...(projectsResponse.data || []).map((row) => row.liquidated_sale_id),
    ...(milestonesResponse.data || []).map((row) => row.triggered_sale_id),
    ...(ticketsResponse.data || []).map((row) => row.liquidated_sale_id),
  ];
  const salesResponse = await fetchRowsByIds(
    (chunk) => supabase.from('sales').select('id, total, active, invoice_status, billing_source, created_at').eq('organization_id', organizationId).eq('active', true).in('id', chunk),
    crmSaleIds,
    { label: 'reportSales' },
  );

  // sales_receivable_applications no tiene organization_id directo, así que filtramos
  // por receivable_id usando los receivables ya cargados para la organización.
  const receivableIdsForOrg = (unwrapOptionalRows('reportSalesReceivables', salesReceivablesResponse, 'sales_receivables') || [])
    .map((row) => row.id)
    .filter(Boolean);
  // Chunkeado: `receivable_id=in.(...)` viaja en la URL y con más de ~400 UUIDs
  // el request se cae.
  const filteredApplicationsResponse = await fetchRowsByIds(
    (chunk) => supabase.from('sales_receivable_applications').select('id, receivable_id, amount, created_at').in('receivable_id', chunk).order('id', { ascending: true }),
    receivableIdsForOrg,
    { label: 'reportSalesReceivableApplications' },
  );

  const profiles = await fetchCrmReportProfiles(organizationId);

  return {
    profiles,
    timeEntries: unwrapResponse('reportTimeEntries', timeEntriesResponse) || [],
    capacityRows: unwrapOptionalRows('reportCapacity', capacityResponse, 'crm_user_capacity_calendar'),
    projects: unwrapResponse('reportProjects', projectsResponse) || [],
    milestones: unwrapOptionalRows('reportMilestones', milestonesResponse, 'crm_project_milestones'),
    projectTypes: unwrapResponse('reportProjectTypes', projectTypesResponse) || [],
    stages: unwrapResponse('reportStages', stagesResponse) || [],
    tickets: unwrapResponse('reportTickets', ticketsResponse) || [],
    ticketTypes: unwrapResponse('reportTicketTypes', ticketTypesResponse) || [],
    ticketStatuses: unwrapResponse('reportTicketStatuses', ticketStatusesResponse) || [],
    tasks: unwrapResponse('reportTicketTasks', tasksResponse) || [],
    ticketAssignments: unwrapOptionalRows('reportTicketAssignments', ticketAssignmentsResponse, 'crm_ticket_assignments'),
    taskAssignments: unwrapOptionalRows('reportTaskAssignments', taskAssignmentsResponse, 'crm_ticket_task_assignments'),
    taskFinalizations: unwrapOptionalRows('reportTaskFinalizations', taskFinalizationsResponse, 'crm_ticket_task_finalizations'),
    agreements: unwrapOptionalRows('reportAgreements', agreementsResponse, 'crm_commercial_agreements'),
    agreementItems: unwrapOptionalRows('reportAgreementItems', agreementItemsResponse, 'crm_commercial_agreement_items'),
    schedules: unwrapOptionalRows('reportSchedules', schedulesResponse, 'crm_billing_schedules'),
    invoices: unwrapOptionalRows('reportInvoices', invoicesResponse, 'crm_invoices'),
    invoiceLines: unwrapOptionalRows('reportInvoiceLines', invoiceLinesResponse, 'crm_invoice_lines'),
    clients: unwrapResponse('reportClients', clientsResponse) || [],
    sales: unwrapOptionalRows('reportSales', salesResponse, 'sales'),
    salesReceivables: unwrapOptionalRows('reportSalesReceivables', salesReceivablesResponse, 'sales_receivables'),
    salesReceivableApplications: unwrapOptionalRows('reportSalesReceivableApplications', filteredApplicationsResponse, 'sales_receivable_applications'),
    meetings: unwrapOptionalRows('reportMeetings', meetingsResponse, 'crm_meetings'),
    meetingParticipants: unwrapOptionalRows('reportMeetingParticipants', meetingParticipantsResponse, 'crm_meeting_participants'),
    meetingTopics: unwrapOptionalRows('reportMeetingTopics', meetingTopicsResponse, 'crm_meeting_topics'),
    meetingTopicParticipants: unwrapOptionalRows('reportMeetingTopicParticipants', meetingTopicParticipantsResponse, 'crm_meeting_topic_participants'),
    projectExpenses: unwrapOptionalRows('reportProjectExpenses', projectExpensesResponse, 'crm_project_expenses'),
    projectFinancialSummaries: unwrapOptionalRows('reportProjectFinancialSummary', projectFinancialSummaryResponse, 'crm_project_financial_summary'),
    ticketFinancialSummaries: unwrapOptionalRows('reportTicketFinancialSummary', ticketFinancialSummaryResponse, 'crm_ticket_financial_summary'),
    projectExpensesHistorical: unwrapOptionalRows('reportProjectExpensesHistorical', projectExpensesHistoricalResponse, 'crm_project_expenses'),
  };
}

export function getAgreementStatusTone(status) {
  if (status === 'active') return 'emerald';
  if (status === 'draft') return 'amber';
  if (status === 'cancelled') return 'rose';
  return 'slate';
}

export function getCommercialScheduleTone(status) {
  if (status === 'paid') return 'emerald';
  if (status === 'invoiced' || status === 'generated') return 'blue';
  if (status === 'cancelled') return 'rose';
  return 'amber';
}
